import { useState } from 'react';
import { useAncho } from '../lib/useAhora';

export interface SerieBarras { nombre: string; clase: 's1' | 's2' }
export interface FilaBarras { clave: string; etiqueta: string; valores: number[] }

/** Escala "bonita": 0 → máximo redondeado hacia arriba. */
export function maximoBonito(max: number): number {
  if (max <= 0) return 1;
  const e = Math.pow(10, Math.floor(Math.log10(max)));
  const f = max / e;
  const paso = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return paso * e;
}

/** Barra horizontal con extremo de datos redondeado (4 px) y base recta. */
function rutaBarra(x: number, y: number, w: number, h: number, r = 4): string {
  if (w <= 0) return '';
  const rr = Math.min(r, w, h / 2);
  return `M${x} ${y}H${x + w - rr}Q${x + w} ${y} ${x + w} ${y + rr}V${y + h - rr}Q${x + w} ${y + h} ${x + w - rr} ${y + h}H${x}Z`;
}

/**
 * Barras horizontales agrupadas, una escala. Etiquetas a la izquierda, valor al final de cada barra,
 * clave arriba si hay ≥2 series, y tooltip al pasar el cursor. La tabla de la pantalla es la vista accesible.
 */
export function BarrasH({ titulo, filas, series, formato }: {
  titulo: string; filas: FilaBarras[]; series: SerieBarras[]; formato: (n: number) => string;
}) {
  const [ref, ancho] = useAncho<HTMLDivElement>();
  const [tip, setTip] = useState<{ x: number; y: number; texto: string } | null>(null);
  const alto = 14, hueco = 2, padFila = 12;
  const altoFila = series.length * alto + (series.length - 1) * hueco + padFila;
  const etiquetaW = Math.min(170, Math.round(ancho * 0.34));
  const valorW = 64;
  const plotW = Math.max(60, ancho - etiquetaW - valorW - 8);
  const max = maximoBonito(Math.max(0, ...filas.flatMap((f) => f.valores)));
  const escala = (v: number) => (v / max) * plotW;
  const top = 18;
  const altoTotal = top + filas.length * altoFila + 4;
  const recortar = (t: string) => (t.length * 6.6 > etiquetaW - 8 ? t.slice(0, Math.max(3, Math.floor((etiquetaW - 8) / 6.6) - 1)) + '…' : t);
  const ticks = [0, 0.5, 1].map((f) => f * max);

  return (
    <figure className="pila" style={{ gap: 6 }}>
      <figcaption className="fila fila--entre">
        <b style={{ fontSize: '.92rem' }}>{titulo}</b>
        {series.length > 1 && (
          <span className="clave-grafica">
            {series.map((s) => <span key={s.nombre}><i style={{ background: s.clase === 's1' ? 'var(--serie-1)' : 'var(--serie-2)' }} />{s.nombre}</span>)}
          </span>
        )}
      </figcaption>
      <div ref={ref} style={{ position: 'relative' }}>
        {filas.length === 0 ? <p className="vacio">Sin datos en este rango.</p> : (
          <svg className="grafica" width={ancho} height={altoTotal} viewBox={`0 0 ${ancho} ${altoTotal}`} role="img" aria-label={titulo}
            onMouseLeave={() => setTip(null)}>
            {ticks.map((t, i) => (
              <g key={i}>
                <line className="rej" x1={etiquetaW + escala(t)} x2={etiquetaW + escala(t)} y1={top - 4} y2={altoTotal - 4} />
                <text x={etiquetaW + escala(t)} y={10} textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}>{formato(t)}</text>
              </g>
            ))}
            {filas.map((f, i) => {
              const y0 = top + i * altoFila + padFila / 2;
              const texto = `${f.etiqueta}: ${series.map((s, k) => `${s.nombre} ${formato(f.valores[k] ?? 0)}`).join(' · ')}`;
              return (
                <g key={f.clave} className="barra"
                  onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, texto })}>
                  <rect className="hit" x={0} y={y0 - padFila / 2} width={ancho} height={altoFila} rx={6} />
                  <text className="etq" x={etiquetaW - 8} y={y0 + (altoFila - padFila) / 2 + 4} textAnchor="end">{recortar(f.etiqueta)}<title>{texto}</title></text>
                  {series.map((s, k) => {
                    const v = f.valores[k] ?? 0;
                    const y = y0 + k * (alto + hueco);
                    const w = escala(v);
                    return (
                      <g key={s.nombre}>
                        <path className={s.clase} d={rutaBarra(etiquetaW, y, Math.max(w, v > 0 ? 2 : 0), alto)} />
                        <text className="valor" x={etiquetaW + w + 5} y={y + alto - 3}>{formato(v)}</text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
            <line className="eje" x1={etiquetaW} x2={etiquetaW} y1={top - 4} y2={altoTotal - 4} />
          </svg>
        )}
      </div>
      {tip && <div className="tooltip" style={{ left: tip.x + 12, top: tip.y + 12 }}>{tip.texto}</div>}
    </figure>
  );
}
